import { useLocalStorageState } from '@/hooks/useSessionState';
import { translate, translateSafe } from '@/utils/translations';
import { useCallback, useEffect, useMemo } from 'react';
import { useAliasedKindName, KindAliasFn } from '@/contexts/alias';
import { getConversationAvailableCustomColumns } from '@/api/generated/conversations/conversations';
import { getAvailableCustomColumns, getAvailableTags } from '@/api/generated/interactions/interactions';

import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { useProjectsContext } from '@/providers/projects-provider';
import { useQuery } from '@tanstack/react-query';
import { capitalize } from 'lodash';
import { CUSTOM_COLUMN_PREFIX, CONVERSATION_CUSTOM_COLUMN_PREFIX, TAG_PREFIX } from '@/models/common/filters';
import { getDatatableVariableIconKey } from '@/domains/chart/constants/datatable-variable-slug-to-icon-key';

// depends on AggregateVariable
export type Column =
  | 'users'
  | 'interactions'
  | 'retention'
  | 'sentiment_score'
  | 'conversation_sentiment_score'
  | 'positive'
  | 'negative'
  | 'conversation_error_rate'
  | 'bounce_rate'
  | 'positive_messages'
  | 'negative_messages'
  | 'session_length'
  | 'session_time'
  | 'n_sessions'
  | 'session_start'
  | 'thumbs_up'
  | 'thumbs_down'
  | 'timestamp'
  | 'input_text'
  | 'output_text'
  | 'end_user'
  | 'rag_details'
  | 'interaction_id'
  | 'model_suggestion'
  | 'input_length'
  | 'output_length'
  | 'input_tokens'
  | 'output_tokens'
  | string;

export type ColumnItem = { value: Column; title: string; icon: string; deletable: boolean; enabled: boolean };

const getAllColumnsItems = (aggregateBy: AggregateBy): ColumnItem[] =>
  (
    [
      { value: 'users', title: 'Users', deletable: false, enabled: true },
      { value: 'end_user', title: translateSafe('variable', 'end_user'), deletable: false, enabled: true },
      {
        value: 'retention',
        title: translateSafe('variable', 'retention'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'interactions',
        title: translateSafe('variable', 'interactions'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'n_sessions',
        title: translateSafe('variable', 'n_sessions'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'negative',
        title: translateSafe('userIntellMetric', 'negative-feedback'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'conversation_error_rate',
        title: translateSafe('variable', 'conversation_error_rate'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'bounce_rate',
        title: translateSafe('variable', 'bounce_rate'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'error_rate_explicit',
        title: translateSafe('variable', 'error_rate_explicit'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'conversation_error_rate_explicit',
        title: translateSafe('variable', 'conversation_error_rate_explicit'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'sentiment_score',
        title: translateSafe('variable', 'sentiment_score'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'conversation_sentiment_score',
        title: translateSafe('variable', 'conversation_sentiment_score'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'positive_messages',
        title: translateSafe('variable', 'positive_messages'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'negative_messages',
        title: translateSafe('variable', 'negative_messages'),
        deletable: false,
        enabled: true,
      },
      // {
      //   value: 'positive',
      //   title: 'Positive feedback',
      //   Icon: <ArrowUpCircle size={14} className="mr-1 inline" />,
      // },
      // TODO: Add back
      {
        value: 'session_length',
        title: translateSafe(
          'variable',
          aggregateBy === 'interaction' ? 'session_length' : 'session_length_conversations',
        ),
        deletable: false,
        enabled: true,
      },
      // {
      //   value: 'session_start',
      //   title: translateSafe('variable', 'start_session'),
      //   Icon: <Clock size={14} className="mr-1 inline" />,
      // },
      {
        value: 'session_time',
        title: translateSafe('variable', 'session_time'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'start_session',
        title: translateSafe('variable', 'start_session'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'end_session',
        title: translateSafe('variable', 'end_session'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'thumbs_up',
        title: translateSafe('variable', 'positive_exp_feedback'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'thumbs_down',
        title: translateSafe('variable', 'negative_exp_feedback'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'explicit_feedback_ratio',
        title: translateSafe('variable', 'explicit_feedback_ratio'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'paste_output',
        title: translateSafe('variable', 'paste_output'),
        deletable: false,
        enabled: true,
      },
      // INTERACTIONS
      {
        value: 'timestamp',
        title: translateSafe('variable', 'timestamp'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'input_text',
        title: translateSafe('variable', 'input_text'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'output_text',
        title: translateSafe('variable', 'output_text'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'end_user_z',
        title: translateSafe('variable', 'end_user'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'cost',
        title: translateSafe('interaction_properties', 'cost'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'latency',
        title: translateSafe('interaction_properties', 'latency'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'text_language',
        title: translateSafe('interaction_properties', 'text_language'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'verbosity',
        title: translateSafe('variable', 'output_length'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'input_length',
        title:
          aggregateBy === 'conversation'
            ? translateSafe('variable_conversations', 'input_length')
            : translateSafe('interaction_properties', 'input_length'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'output_length',
        title:
          aggregateBy === 'conversation'
            ? translateSafe('variable_conversations', 'output_length')
            : translateSafe('variable', 'output_length'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'pii_removed',
        title: translateSafe('interaction_properties', 'pii_removed'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'feedback_action_text',
        title: translateSafe('interaction_properties', 'feedback_action_text'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'feedback_action_rating',
        title: translateSafe('interaction_properties', 'feedback_action_rating'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'feedback_actions',
        title: translateSafe('interaction_properties', 'feedback_actions'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'emotions',
        title: translateSafe('interaction_properties', 'emotions'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'sentiment',
        title: translateSafe('interaction_properties', 'sentiment'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'conversation_id',
        title: translateSafe('variable', 'conversation_id'),
        deletable: false,
        enabled: true,
      },
      {
        value: 'interaction_id',
        title: 'Interaction ID',
        deletable: false,
        enabled: false,
      },
      {
        value: 'topic',
        title: translateSafe('variable', 'topic'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'subtopic',
        title: translateSafe('variable', 'subtopic'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'model_suggestion',
        title: translateSafe('variable', 'type_of_problem_v2'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'user_action',
        title: translateSafe('variable', 'user_action'),
        deletable: false,
        enabled: false,
      },

      {
        value: 'topics_conversations',
        title: translateSafe('variable', 'topic'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'n_interactions',
        title: translateSafe('variable', 'dialogue_turns'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'duration',
        title: translateSafe('variable', 'duration'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'dropoff_sentiment',
        title: translateSafe('variable', 'dropoff_sentiment'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'conversation_error_status',
        title: translateSafe('variable', 'error_status'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'rag_details',
        title: translateSafe('variable', 'rag_details'),
        deletable: false,
        enabled: false,
      },
      {
        value: 'input_tokens',
        title: 'Input Tokens',
        deletable: false,
        enabled: false,
      },
      {
        value: 'output_tokens',
        title: 'Output Tokens',
        deletable: false,
        enabled: false,
      },
      {
        value: 'labels',
        title: 'Labels',
        deletable: false,
        enabled: false,
      },
    ] satisfies Omit<ColumnItem, 'icon'>[]
  ).map((item) => ({ ...item, icon: getDatatableVariableIconKey(item.value as string) }));

function arraysHaveSameElements<T>(arr1: T[], arr2: T[]): boolean {
  return arr1.length === arr2.length && [...arr1].sort().every((value, index) => value === [...arr2].sort()[index]);
}

// Titles are persisted in local storage, so aliases are applied on read to always reflect the latest values.
function applyAliasToColumnItems(items: ColumnItem[], alias: KindAliasFn): ColumnItem[] {
  return items.map((item) => {
    const aliasedTitle = alias(item.value);
    return aliasedTitle ? { ...item, title: aliasedTitle } : item;
  });
}

export const useAvailableInteractionColumnsState = () => {
  const { selectedProject } = useProjectsContext();
  const { timeRange } = useGlobalFiltersContext();
  const aliasKindName = useAliasedKindName();

  // Fetch interaction columns with query
  const { data: interactionColumns } = useQuery({
    queryKey: ['getCustomColumns', selectedProject?.id],
    queryFn: ({ signal }) => getAvailableCustomColumns({ project_id: selectedProject?.id ?? '' }, { signal }),
    keepPreviousData: true,
    enabled: !!selectedProject?.id,
  });

  const { data: tags } = useQuery({
    queryKey: ['getAvailableTags', selectedProject?.id, timeRange],
    queryFn: ({ signal }) =>
      getAvailableTags({ project_id: selectedProject?.id ?? '', filters: [], time_range: timeRange }, { signal }),
    keepPreviousData: true,
    enabled: !!selectedProject?.id,
  });

  // Use local storage for filtered column items
  const [filteredColumnItems, setFilteredColumnItems] = useLocalStorageState<ColumnItem[]>(
    'filteredInteractionColumnItems',
    [],
  );

  // Helper to build filtered column items
  const buildFilteredColumnItems = useCallback((columnsToAdd: string[]): ColumnItem[] => {
    const defaultColumns = getDefaultInteractionColumns();
    const additionalColumns = getAdditionalInteractionColumns();
    const defaultItems = getAllColumnsItems('interaction').filter((e) => defaultColumns.includes(e.value));
    const additionalItems = getAllColumnsItems('interaction').filter((e) => additionalColumns.includes(e.value));

    // Keep deletable items not in columnsToRemove and add new columnsToAdd
    const newItems = columnsToAdd.map((value) => ({
      value,
      title: capitalize(
        value.startsWith(TAG_PREFIX) || value.startsWith(CUSTOM_COLUMN_PREFIX)
          ? value.replace(TAG_PREFIX, '').replace(CUSTOM_COLUMN_PREFIX, '').replace('_', ' ')
          : translate('interaction_properties', value),
      ),
      icon:
        value.startsWith(TAG_PREFIX) || value.startsWith(CUSTOM_COLUMN_PREFIX)
          ? 'default'
          : getDatatableVariableIconKey(value),
      deletable: value.startsWith(CUSTOM_COLUMN_PREFIX),
      enabled: true,
    }));

    return [...defaultItems, ...additionalItems, ...newItems];
  }, []);

  // Update the filtered column items in local storage
  const updateFilteredColumnItems = useCallback(
    (columnsToAdd: string[]) => {
      const updatedColumns = buildFilteredColumnItems(columnsToAdd);
      setFilteredColumnItems(updatedColumns);
    },
    [buildFilteredColumnItems, setFilteredColumnItems],
  );

  // Check and update filtered columns when fetching data
  useEffect(() => {
    if (!interactionColumns || !tags) return;

    const defaultColumns = getDefaultInteractionColumns();
    const additionalColumns = getAdditionalInteractionColumns();
    const currentColumns = filteredColumnItems.map((e) => e.value);
    const totalColumns = [
      ...defaultColumns,
      ...additionalColumns,
      ...(interactionColumns?.custom_columns?.map((col) => `${CUSTOM_COLUMN_PREFIX}${col}`) ?? []),
      ...(tags?.tags.map((tag) => `${TAG_PREFIX}${tag}`) ?? []),
    ];

    if (!arraysHaveSameElements(totalColumns, currentColumns)) {
      // Update filtered columns by adding missing ones and removing extra ones
      const columnsToAdd = interactionColumns?.custom_columns?.map((col) => `${CUSTOM_COLUMN_PREFIX}${col}`) ?? [];

      const tagsToAdd = tags?.tags.map((tag) => `${TAG_PREFIX}${tag}`) ?? [];

      return updateFilteredColumnItems([...columnsToAdd, ...tagsToAdd]);
    }
  }, [interactionColumns, filteredColumnItems, updateFilteredColumnItems, tags]);

  // Manage state for available interaction columns
  const [availableColumns, setAvailableColumns] = useLocalStorageState<string[]>(
    'availableInteractionColumns',
    getDefaultInteractionColumns(),
  );

  const aliasedColumnItems = useMemo(
    () => applyAliasToColumnItems(filteredColumnItems, aliasKindName),
    [filteredColumnItems, aliasKindName],
  );

  return [availableColumns, setAvailableColumns, aliasedColumnItems, setFilteredColumnItems] as const;
};

export const useAvailableConversationColumnsState = () => {
  const { selectedProject } = useProjectsContext();
  const { timeRange } = useGlobalFiltersContext();
  const aliasKindName = useAliasedKindName();

  // Fetch custom columns for conversation columns as well
  const { data: conversationColumns } = useQuery({
    queryKey: ['getConversationCustomColumns', selectedProject?.id],
    queryFn: ({ signal }) =>
      getConversationAvailableCustomColumns({ project_id: selectedProject?.id ?? '' }, { signal }),
    keepPreviousData: true,
    enabled: !!selectedProject?.id,
  });

  const { data: tags } = useQuery({
    queryKey: ['getAvailableTags', selectedProject?.id, timeRange],
    queryFn: ({ signal }) =>
      getAvailableTags({ project_id: selectedProject?.id ?? '', filters: [], time_range: timeRange }, { signal }),
    keepPreviousData: true,
    enabled: !!selectedProject?.id,
  });

  // Use local storage for filtered column items
  const [filteredColumnItems, setFilteredColumnItems] = useLocalStorageState<ColumnItem[]>(
    'filteredConversationColumnItems',
    [],
  );

  // Helper to build filtered column items
  const buildFilteredColumnItems = useCallback((columnsToAdd: string[]): ColumnItem[] => {
    const defaultColumns = getDefaultConversationColumns();
    const additionalColumns = getAdditionalConversationColumns();
    const allConversation = getAllColumnsItems('conversation');
    const defaultItems = defaultColumns
      .map((col) => allConversation.find((e) => e.value === col))
      .filter((e): e is ColumnItem => e !== undefined);
    const additionalItems = allConversation.filter((e) => additionalColumns.includes(e.value));

    // Keep deletable items not in columnsToRemove and add new columnsToAdd
    const newItems = columnsToAdd.map((value) => ({
      value,
      title: capitalize(
        value.startsWith(TAG_PREFIX) || value.startsWith(CONVERSATION_CUSTOM_COLUMN_PREFIX)
          ? value.replace(TAG_PREFIX, '').replace(CONVERSATION_CUSTOM_COLUMN_PREFIX, '').replace('_', ' ')
          : translate('interaction_properties', value),
      ),
      icon:
        value.startsWith(TAG_PREFIX) || value.startsWith(CONVERSATION_CUSTOM_COLUMN_PREFIX)
          ? 'default'
          : getDatatableVariableIconKey(value),
      deletable: value.startsWith(CONVERSATION_CUSTOM_COLUMN_PREFIX),
      enabled: true,
    }));

    return [...defaultItems, ...additionalItems, ...newItems];
  }, []);

  // Update the filtered column items in local storage
  const updateFilteredColumnItems = useCallback(
    (columnsToAdd: string[]) => {
      const updatedColumns = buildFilteredColumnItems(columnsToAdd);
      setFilteredColumnItems(updatedColumns);
    },
    [buildFilteredColumnItems, setFilteredColumnItems],
  );

  // Check and update filtered columns when fetching data
  useEffect(() => {
    if (!tags || !conversationColumns) return;

    const defaultColumns = getDefaultConversationColumns();
    const additionalColumns = getAdditionalConversationColumns();
    const currentColumns = filteredColumnItems.map((e) => e.value);
    const columnsToAdd =
      conversationColumns?.custom_columns?.map((col) => `${CONVERSATION_CUSTOM_COLUMN_PREFIX}${col}`) ?? [];
    const tagsToAdd = tags?.tags.map((tag) => `${TAG_PREFIX}${tag}`) ?? [];
    const totalColumns = [...defaultColumns, ...additionalColumns, ...columnsToAdd, ...tagsToAdd];

    // Only update if the arrays don't have the same elements
    // This prevents the infinite loop by avoiding unnecessary state updates
    if (!arraysHaveSameElements(totalColumns, currentColumns)) {
      updateFilteredColumnItems([...columnsToAdd, ...tagsToAdd]);
    }
  }, [tags, conversationColumns, updateFilteredColumnItems, filteredColumnItems]);

  // Manage state for available conversation columns
  const [availableColumns, setAvailableColumns] = useLocalStorageState<string[]>(
    'availableConversationColumns',
    getDefaultConversationColumns(),
  );

  const aliasedColumnItems = useMemo(
    () => applyAliasToColumnItems(filteredColumnItems, aliasKindName),
    [filteredColumnItems, aliasKindName],
  );

  return [availableColumns, setAvailableColumns, aliasedColumnItems, setFilteredColumnItems] as const;
};

export function getDefaultInteractionColumns(): Column[] {
  return ['timestamp', 'input_text', 'output_text'];
}

export function getDefaultConversationColumns(): Column[] {
  // TODO: currently dropoff sentiment is calculated on the frontend for every item of the conversation,
  // we need something like topics_conversations for sentiment and implement on backend
  return [
    'conversation_id',
    'conversation_error_status',
    'topics_conversations',
    'n_interactions',
    'duration',
    'dropoff_sentiment',
  ];
}

function getAdditionalInteractionColumns(): Column[] {
  return [
    'cost',
    'topic',
    'subtopic',
    'model_suggestion',
    'user_action',
    'latency',
    'text_language',
    'verbosity',
    'pii_removed',
    'feedback_action_text',
    'feedback_action_rating',
    'feedback_actions',
    'emotions',
    'sentiment',
    'end_user',
    'rag_details',
    'conversation_id',
    'interaction_id',
    'input_length',
    'input_tokens',
    'output_tokens',
    'labels',
  ];
}

function getAdditionalConversationColumns(): Column[] {
  // TODO: add end_user to aggregation query and backend if needed
  return [
    'end_user_z',
    'start_session',
    'end_session',
    'input_length',
    'output_length',
    'input_tokens',
    'output_tokens',
    'labels',
  ];
}
